import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import { ApiError } from 'shared/api/client.js';
import { PasswordInput } from 'shared/components/PasswordInput/index.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import LanguageToggle from '../components/LanguageToggle.jsx';
import styles from './LoginPage.module.css';

const CREDENTIAL_ERROR_IDS = {
  INVALID_CREDENTIALS: 'login.invalidCredentials',
  ACCOUNT_INACTIVE: 'login.accountInactive',
  LOGIN_RATE_LIMITED: 'login.rateLimited',
};

const TWO_FACTOR_ERROR_IDS = {
  TWO_FACTOR_INVALID: 'login.twoFactor.invalid',
  TWO_FACTOR_RATE_LIMITED: 'login.twoFactor.rateLimited',
  NO_PENDING_LOGIN: 'login.twoFactor.expired',
};

// Security-hardening task, item 3: an admin with 2FA enabled gets a second
// step here after the password step succeeds (authService.login returns
// { requiresTwoFactor: true } instead of a session — see
// context/AuthContext.jsx's own comment). `step` is local UI state only;
// the actual security boundary is server-side (the session's PENDING
// marker, routes/authRoutes.js), matching admin/CLAUDE.md's "role gating is
// enforced by the API, not by hiding UI" stance applied here to login steps.
export default function LoginPage() {
  const {
    login,
    isLoggingIn,
    loginError,
    verifyTwoFactor,
    isVerifyingTwoFactor,
    verifyTwoFactorError,
  } = useAuth();
  const { t, lang } = useLanguage();
  const [step, setStep] = useState('password');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const passwordForm = useForm({ defaultValues: { email: '', password: '' } });
  const twoFactorForm = useForm({ defaultValues: { code: '' } });

  const onSubmitPassword = async (values) => {
    try {
      const result = await login(values);
      if (result && result.requiresTwoFactor) {
        setStep('twoFactor');
      }
    } catch {
      // loginError (from useAuth) already carries the ApiError for display
      // below — nothing further to do here, just stop the unhandled
      // rejection from surfacing.
    }
  };

  const onSubmitTwoFactor = async (values) => {
    try {
      await verifyTwoFactor(
        useRecoveryCode ? { recoveryCode: values.code.trim() } : { token: values.code.trim() }
      );
    } catch {
      // verifyTwoFactorError already carries the ApiError for display below.
    }
  };

  // When there's no specific message for the code the API returned, fall
  // back to the generic message but name the code alongside it — a user
  // reporting a failed sign-in should be able to say what failed without
  // opening devtools (the full response is logged there by
  // shared/api/client.js).
  const errorText = (err, idMap) => {
    if (!(err instanceof ApiError)) return null;
    const mapped = idMap[err.code];
    if (mapped) return t(mapped);
    return err.code ? `${t('error.INTERNAL_ERROR')} (${err.code})` : t('error.INTERNAL_ERROR');
  };

  const passwordErrorText = errorText(loginError, CREDENTIAL_ERROR_IDS);
  const twoFactorErrorText = errorText(verifyTwoFactorError, TWO_FACTOR_ERROR_IDS);

  if (step === 'twoFactor') {
    return (
      <div className={styles.page}>
        <div className={styles.toggleRow}>
          <LanguageToggle />
        </div>
        <form
          className={styles.card}
          onSubmit={twoFactorForm.handleSubmit(onSubmitTwoFactor)}
          noValidate
        >
          <h1 className={styles.title}>{t('app.brand')}</h1>
          <p className={styles.subtitle}>{t('login.twoFactor.title')}</p>

          <label className={styles.field}>
            <span className={styles.label}>
              {useRecoveryCode
                ? t('login.twoFactor.recoveryCodeLabel')
                : t('login.twoFactor.codeLabel')}
            </span>
            <input
              type="text"
              inputMode={useRecoveryCode ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              autoFocus
              {...twoFactorForm.register('code', { required: true })}
              className={styles.input}
            />
          </label>

          {twoFactorErrorText && <p className={styles.formError}>{twoFactorErrorText}</p>}

          <button type="submit" className={styles.submitButton} disabled={isVerifyingTwoFactor}>
            {isVerifyingTwoFactor && <Spinner />}{' '}
            {isVerifyingTwoFactor ? t('login.twoFactor.submitting') : t('login.twoFactor.submit')}
          </button>

          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setUseRecoveryCode((prev) => !prev)}
          >
            {useRecoveryCode
              ? t('login.twoFactor.useCodeInstead')
              : t('login.twoFactor.useRecoveryCodeInstead')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.toggleRow}>
        <LanguageToggle />
      </div>
      <form
        className={styles.card}
        onSubmit={passwordForm.handleSubmit(onSubmitPassword)}
        noValidate
      >
        <h1 className={styles.title}>{t('app.brand')}</h1>
        <p className={styles.subtitle}>{t('login.title')}</p>

        <label className={styles.field}>
          <span className={styles.label}>{t('login.email')}</span>
          <input
            type="email"
            autoComplete="username"
            {...passwordForm.register('email', { required: true })}
            className={styles.input}
          />
          {passwordForm.formState.errors.email && (
            <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t('login.password')}</span>
          <PasswordInput
            lang={lang}
            autoComplete="current-password"
            {...passwordForm.register('password', { required: true })}
          />
          {passwordForm.formState.errors.password && (
            <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
          )}
        </label>

        {passwordErrorText && <p className={styles.formError}>{passwordErrorText}</p>}

        <button type="submit" className={styles.submitButton} disabled={isLoggingIn}>
          {isLoggingIn && <Spinner />} {isLoggingIn ? t('login.submitting') : t('login.submit')}
        </button>
      </form>
    </div>
  );
}
